/**
 * opfs_index — the pure bookkeeping of the persistent asset cache
 * (settlement LOD0 research L3, 2026-09-03). The worker keeps the bytes in
 * the origin private file system; this module decides what to keep.
 *
 * Every cached object is content-addressed on the store (its key carries a
 * sha256), so an entry is valid forever: the only reasons to drop one are
 * the byte budget and the browser's own quota. Eviction is least recently
 * USED (a hit refreshes the stamp), oldest first, until the incoming object
 * fits under the budget.
 */

export interface CacheEntry { uri: string; file: string; size: number; at: number }
export interface CacheIndex { version: 1; entries: Record<string, CacheEntry>; bytes: number }

/** Default byte budget of the persistent cache (packs + tiers + skins of a couple of civilizations). */
export const OPFS_CACHE_BUDGET = 768 * 1024 * 1024;
/** Objects above this size are not cached (a stray 100 MB upload would churn the whole budget). */
export const OPFS_CACHE_MAX_OBJECT = 96 * 1024 * 1024;

export function emptyIndex(): CacheIndex { return { version: 1, entries: {}, bytes: 0 }; }

/** A stable file name for a uri (OPFS entries are flat; keep the sha when the key has one). */
export function fileNameFor(uri: string): string {
  const m = /([0-9a-f]{64})/.exec(uri);
  const ext = /\.([a-z0-9]+)$/i.exec(uri)?.[1] ?? 'bin';
  if (m) return `${m[1]}-${uri.length}.${ext}`; // the length disambiguates two keys sharing one sha (a tier vs its source)
  let h = 2166136261;
  for (let i = 0; i < uri.length; i++) h = Math.imul(h ^ uri.charCodeAt(i), 16777619) >>> 0;
  return `u${h.toString(16)}-${uri.length}.${ext}`;
}

/** Which entries to evict so `incoming` bytes fit under `budget`: least recently used first. Returns uris. */
export function planEviction(index: CacheIndex, incoming: number, budget = OPFS_CACHE_BUDGET): string[] {
  if (index.bytes + incoming <= budget) return [];
  const sorted = Object.values(index.entries).sort((a, b) => a.at - b.at);
  const out: string[] = [];
  let bytes = index.bytes;
  for (const e of sorted) {
    if (bytes + incoming <= budget) break;
    out.push(e.uri); bytes -= e.size;
  }
  return out;
}

/** Record a stored object (replacing an older entry for the same uri). */
export function indexPut(index: CacheIndex, uri: string, size: number, now: number): CacheIndex {
  const prev = index.entries[uri];
  const bytes = index.bytes - (prev?.size ?? 0) + size;
  return { version: 1, entries: { ...index.entries, [uri]: { uri, file: fileNameFor(uri), size, at: now } }, bytes };
}

/** Drop entries (by uri). */
export function indexDrop(index: CacheIndex, uris: string[]): CacheIndex {
  const entries = { ...index.entries };
  let bytes = index.bytes;
  for (const u of uris) { const e = entries[u]; if (e) { bytes -= e.size; delete entries[u]; } }
  return { version: 1, entries, bytes: Math.max(0, bytes) };
}

/** Refresh an entry's recency on a hit. */
export function indexTouch(index: CacheIndex, uri: string, now: number): CacheIndex {
  const e = index.entries[uri];
  if (!e) return index;
  return { ...index, entries: { ...index.entries, [uri]: { ...e, at: now } } };
}

/** Is this key one the cache should hold at all (content-addressed, under the size cap)? */
export function cacheable(uri: string, size: number, maxObject = OPFS_CACHE_MAX_OBJECT): boolean {
  return /[0-9a-f]{64}/.test(uri) && size > 0 && size <= maxObject;
}
